import { useDocumentTitle } from "@/lib/use-document-title";
import { useEffect, useState } from "react";
import { StaffLayout } from "@/components/StaffLayout";
import { supabase } from "@/integrations/supabase/client";
import { useStores } from "@/lib/data-hooks";
import { Plus, Trash2, X, Shield, User as UserIcon, Power } from "lucide-react";
import { useConfirm } from "@/components/ConfirmDialog";

type StaffRow = {
  id: string;
  user_id: string | null;
  email: string;
  full_name: string | null;
  role: "staff" | "admin";
  store_id: string | null;
  active: boolean;
  created_at: string;
};

async function call(action: string, payload: Record<string, unknown>) {
  const { data, error } = await supabase.functions.invoke("manage-staff", {
    body: { action, ...payload },
  });
  if (error) throw error;
  if ((data as any)?.error) throw new Error((data as any).error);
  return data;
}

export default function PersonalPage() {
  useDocumentTitle("Personal & behörigheter — ÅF-Portal");
  const { data: stores = [] } = useStores();
  const [rows, setRows] = useState<StaffRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [showAdd, setShowAdd] = useState(false);

  async function load() {
    setLoading(true);
    const { data, error } = await supabase.from("staff" as any).select("*").order("created_at", { ascending: false });
    if (error) setErr(error.message);
    else setRows(((data ?? []) as unknown) as StaffRow[]);
    setLoading(false);
  }

  useEffect(() => { load(); }, []);

  const storeMap = new Map(stores.map(s => [s.id, s.name]));

  async function setRole(r: StaffRow, role: "staff" | "admin") {
    try {
      const patch: Record<string, unknown> = { id: r.id, role };
      if (role === "admin") patch.store_id = null; // admin har ingen specifik butik
      await call("update", patch);
      await load();
    } catch (e) { alert((e as Error).message); }
  }
  async function toggleActive(r: StaffRow) {
    try { await call("update", { id: r.id, active: !r.active }); await load(); } catch (e) { alert((e as Error).message); }
  }
  async function setStore(r: StaffRow, store_id: string | null) {
    try { await call("update", { id: r.id, store_id }); await load(); } catch (e) { alert((e as Error).message); }
  }
  const confirm = useConfirm();
  async function remove(r: StaffRow) {
    const ok = await confirm({ title: `Ta bort ${r.email}?`, description: "Konto och inloggning raderas permanent.", confirmText: "Ta bort", destructive: true });
    if (!ok) return;
    try { await call("delete", { id: r.id }); await load(); } catch (e) { alert((e as Error).message); }
  }

  return (
    <StaffLayout title="Personal & behörigheter">
      <p className="text-muted-foreground mb-6 max-w-2xl">Hantera butikspersonal och deras roller. Endast administratörer ser denna vy.</p>

      <div className="flex justify-between items-center mb-4">
        <div className="text-sm text-muted-foreground">{rows.length} personer</div>
        <button onClick={() => setShowAdd(true)} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[var(--color-primary)] text-white text-sm font-display hover:bg-[var(--color-primary-hover)] transition">
          <Plus size={16} /> Lägg till personal
        </button>
      </div>

      {err && <p className="text-destructive text-sm mb-3">{err}</p>}

      <div className="bg-white rounded-xl border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-left">
            <tr>
              <th className="px-4 py-3 font-display">Namn</th>
              <th className="px-4 py-3 font-display">E-post</th>
              <th className="px-4 py-3 font-display">Roll</th>
              <th className="px-4 py-3 font-display">Butik</th>
              <th className="px-4 py-3 font-display">Status</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td className="px-4 py-6 text-muted-foreground" colSpan={6}>Laddar…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td className="px-4 py-6 text-muted-foreground" colSpan={6}>Ingen personal registrerad ännu.</td></tr>
            ) : rows.map(r => (
              <tr key={r.id} className="border-t">
                <td className="px-4 py-3">{r.full_name || <span className="text-muted-foreground">—</span>}</td>
                <td className="px-4 py-3">{r.email}</td>
                <td className="px-4 py-3">
                  <select
                    value={r.role}
                    onChange={e => setRole(r, e.target.value as "staff" | "admin")}
                    className="border rounded-md px-2 py-1 text-xs bg-white"
                  >
                    <option value="staff">Staff</option>
                    <option value="admin">Admin</option>
                  </select>
                </td>
                <td className="px-4 py-3">
                  {r.role === "admin" ? (
                    <span className="text-xs text-muted-foreground italic">Admin (alla butiker)</span>
                  ) : (
                    <select
                      value={r.store_id ?? ""}
                      onChange={e => setStore(r, e.target.value || null)}
                      className="border rounded-md px-2 py-1 text-xs bg-white"
                    >
                      <option value="">—</option>
                      {stores.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                  )}
                </td>
                <td className="px-4 py-3">
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] ${r.active ? "bg-emerald-500/15 text-emerald-700" : "bg-zinc-400/15 text-zinc-500"}`}>
                    {r.active ? "Aktiv" : "Inaktiv"}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => toggleActive(r)} title={r.active ? "Avaktivera" : "Återaktivera"} className="p-1.5 hover:bg-muted rounded-md inline-flex items-center mr-1">
                    <Power size={14} className={r.active ? "text-emerald-600" : "text-zinc-400"} />
                  </button>
                  <button onClick={() => remove(r)} title="Ta bort" className="p-1.5 hover:bg-destructive/10 rounded-md inline-flex items-center">
                    <Trash2 size={14} className="text-destructive" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showAdd && <AddStaffModal onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); load(); }} stores={stores} />}
    </StaffLayout>
  );
}

function AddStaffModal({ onClose, onSaved, stores }: { onClose: () => void; onSaved: () => void; stores: { id: string; name: string }[] }) {
  const [email, setEmail] = useState("");
  const [pwd, setPwd] = useState("");
  const [fullName, setFullName] = useState("");
  const [role, setRole] = useState<"staff" | "admin">("staff");
  const [storeId, setStoreId] = useState<string>("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setErr("");
    if (!email || !pwd) { setErr("E-post och lösenord krävs"); return; }
    if (pwd.length < 8) { setErr("Lösenord måste vara minst 8 tecken"); return; }
    setBusy(true);
    try {
      await call("create", {
        email,
        password: pwd,
        full_name: fullName || null,
        role,
        store_id: role === "admin" ? null : (storeId || null),
      });
      onSaved();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-display font-semibold">Lägg till personal</h2>
          <button onClick={onClose} className="p-1 hover:bg-muted rounded-md"><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="label-field">Namn</label>
            <input className="input-field" value={fullName} onChange={e => setFullName(e.target.value)} />
          </div>
          <div>
            <label className="label-field">E-post</label>
            <input className="input-field" type="email" value={email} onChange={e => setEmail(e.target.value)} required />
          </div>
          <div>
            <label className="label-field">Lösenord (minst 8 tecken)</label>
            <input className="input-field" type="text" value={pwd} onChange={e => setPwd(e.target.value)} required />
          </div>
          <div>
            <label className="label-field">Roll</label>
            <div className="flex gap-2">
              <button type="button" onClick={() => setRole("staff")} className={`flex-1 inline-flex items-center justify-center gap-1.5 py-2 rounded-md border text-sm ${role === "staff" ? "bg-primary text-primary-foreground border-primary" : "bg-white"}`}>
                <UserIcon size={14} /> Staff
              </button>
              <button type="button" onClick={() => setRole("admin")} className={`flex-1 inline-flex items-center justify-center gap-1.5 py-2 rounded-md border text-sm ${role === "admin" ? "bg-primary text-primary-foreground border-primary" : "bg-white"}`}>
                <Shield size={14} /> Admin
              </button>
            </div>
          </div>
          {role === "staff" && (
            <div>
              <label className="label-field">Butik</label>
              <select className="input-field" value={storeId} onChange={e => setStoreId(e.target.value)}>
                <option value="">— Ingen —</option>
                {stores.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
          )}
          {err && <p className="text-destructive text-sm">{err}</p>}
          <button type="submit" disabled={busy} className="btn-pill w-full mt-2">{busy ? "Skapar…" : "Skapa personal"}</button>
        </form>
      </div>
    </div>
  );
}
